import React, { useState } from 'react'

import { Box, Form, Section } from '@factorialco/deprecated-design-system'
import {
  EditSpendingPolicyDetailsFragment,
  EditSpendingPolicyRuleConfigurationFragment,
  EditSpendingPolicyRuleGroupFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { AddRuleModal } from 'modules/finance/components/NewPaymentPoliciesWizard/Limits/AddRuleModal'
import { RuleGroup } from 'modules/finance/components/SpendingPolicies/Detail/SpendingPolicyRulesForm/RuleGroup'

import RemoveModal from './DeleteRuleGroupModal'
import { SpendingPolicyAssigneesForm } from './SpendingPolicyAssigneesForm'
import { SpendingPolicyForm } from './SpendingPolicyForm'
import { CreateRuleGroupProps, OptionItem } from './SpendingPolicyRulesForm/ruleFormHelpers'

interface Props {
  spendingPolicy: EditSpendingPolicyDetailsFragment
  ruleConfigurations: EditSpendingPolicyRuleConfigurationFragment[]
}

export const SpendingPolicyDetail: React.FC<Props> = ({ spendingPolicy, ruleConfigurations }) => {
  const { company } = useCurrentSession()
  const currency = String(company.legalEntities.find((legalEntity) => legalEntity.main)?.currency)

  const [ruleGroupToDelete, setRuleGroupToDelete] =
    useState<EditSpendingPolicyRuleGroupFragment | null>(null)

  const [showAddCategoryModal, setDisplayAddCategoryModal] = useState(false)
  const [showAddSubcategoryModal, setDisplayAddSubcategoryModal] = useState(false)

  const [pendingRuleGroup, setPendingRuleGroup] = useState<CreateRuleGroupProps | null>(null)
  const [pendingChildRuleGroup, setPendingChildRuleGroup] = useState<CreateRuleGroupProps | null>(
    null
  )
  const categoryRuleConfiguration = ruleConfigurations.find(
    (ruleConfiguration) => ruleConfiguration.identifier === 'category'
  )

  const subCategoryRuleConfiguration = ruleConfigurations.find(
    (ruleConfiguration) => ruleConfiguration.identifier === 'subcategory'
  )

  const timeFrameOptions =
    ruleConfigurations.find((ruleConfiguration) => ruleConfiguration.identifier === 'time_frame')
      ?.options ?? []

  const allCategories = categoryRuleConfiguration?.options ?? []
  const allSubcategories = subCategoryRuleConfiguration?.options ?? []

  const selectedCategories = allCategories.filter((subCategory) => {
    return !!subCategory.selected
  })
  const selectedCategoryIds = selectedCategories.map((category) => category.value)

  const availableSubcategories = allSubcategories.filter((subcategory) =>
    selectedCategoryIds.includes(String(subcategory.parentId))
  )
  const subcategoriesNotSelected = availableSubcategories.filter(
    (subCategory) => !subCategory.selected
  )

  const unSelectedCategories = allCategories.filter((category) => {
    return !category.selected
  })

  const clearPendingRuleGroup = () => setPendingRuleGroup(null)
  const clearPendingChildRuleGroup = () => setPendingChildRuleGroup(null)

  const openAddCategoryModal = () => {
    setDisplayAddCategoryModal(true)
  }

  const closeAddCategoryModal = () => {
    setDisplayAddCategoryModal(false)
  }

  const openAddSubcategoryModal = () => {
    setDisplayAddSubcategoryModal(true)
  }

  const closeAddSubcategoryModal = () => {
    setDisplayAddSubcategoryModal(false)
  }

  const addCategories = (categories_selected: OptionItem[]) => {
    const category = categories_selected.at(0)
    category && setPendingRuleGroup(createCategory(category, spendingPolicy, currency))
  }

  const addSubCategories = (categories_selected: OptionItem[]) => {
    const subcategory = categories_selected.at(0)

    const parentRuleGroup = subcategory ? findParentRuleGroup(subcategory) : null
    parentRuleGroup &&
      subcategory &&
      setPendingChildRuleGroup(
        createCategory(subcategory, spendingPolicy, currency, parentRuleGroup)
      )
  }

  const parentRuleGroups = spendingPolicy.spendingPolicyRuleGroups.filter(
    (group) => !group.parentRuleGroup
  )

  const childRuleGroups = spendingPolicy.spendingPolicyRuleGroups.filter(
    (group) => !!group.parentRuleGroup
  )

  const findParentRuleGroup = (subCategory?: OptionItem) => {
    if (!subCategory) return

    const category = allCategories.find((cat) => cat.value == subCategory.parentId?.toString())

    if (!category) return
    return parentRuleGroups.find((group) =>
      group.spendingPolicyRules.some(
        (rule) => rule.identifier === 'category' && rule.value.includes(category.value.toString())
      )
    )
  }
  return (
    <Box>
      <Section.TwoThirds
        title={i18n.t('spending_policies.details.basic_info_title')}
        description={i18n.t('spending_policies.details.basic_info_description')}
        illustration="document-edit"
        paddingX={null}
      >
        <Form withBackground>
          <SpendingPolicyForm spendingPolicy={spendingPolicy} />
        </Form>
      </Section.TwoThirds>
      <Section.TwoThirds
        title={i18n.t('spending_policies.details.limits_title')}
        description={i18n.t('spending_policies.details.limits_description')}
        illustration="control-navigation-adjust-sound"
        paddingX={null}
      >
        <Form withBackground>
          <Box gap="s32">
            <RuleGroup.List
              timeFrameOptions={timeFrameOptions}
              onDelete={(ruleGroup) => setRuleGroupToDelete(ruleGroup)}
              title={i18n.t('spending_policies.wizard.steps.limits.categories')}
              ruleGroups={parentRuleGroups}
            >
              <RuleGroup.New
                timeFrameOptions={timeFrameOptions}
                hasUnselectedCategories={!!unSelectedCategories.length}
                pendingRuleGroup={pendingRuleGroup}
                spendingPolicy={spendingPolicy}
                clearPendingRuleGroup={clearPendingRuleGroup}
                onAdd={openAddCategoryModal}
                actionLabel={i18n.t('spending_policies.details.limits.add_category')}
              />
            </RuleGroup.List>
            {!!availableSubcategories.length && (
              <RuleGroup.List
                timeFrameOptions={timeFrameOptions}
                onDelete={(ruleGroup) => setRuleGroupToDelete(ruleGroup)}
                title={i18n.t('spending_policies.wizard.steps.limits.subcategories')}
                ruleGroups={childRuleGroups}
              >
                <RuleGroup.New
                  timeFrameOptions={timeFrameOptions}
                  hasUnselectedCategories={!!subcategoriesNotSelected.length}
                  pendingRuleGroup={pendingChildRuleGroup}
                  spendingPolicy={spendingPolicy}
                  clearPendingRuleGroup={clearPendingChildRuleGroup}
                  onAdd={openAddSubcategoryModal}
                  actionLabel={i18n.t('spending_policies.details.limits.add_subcategory')}
                />
              </RuleGroup.List>
            )}
          </Box>
        </Form>
      </Section.TwoThirds>
      <Section.TwoThirds
        title={i18n.t('spending_policies.details.people_group_info_title')}
        description={i18n.t('spending_policies.details.people_group_info_description')}
        illustration="people-add"
        paddingX={'s0'}
      >
        <Form withBackground>
          <SpendingPolicyAssigneesForm
            spendingPolicyId={spendingPolicy.id}
            assignees={spendingPolicy.spendingPolicyAssignees ?? null}
          />
        </Form>
      </Section.TwoThirds>
      {showAddCategoryModal && (
        <AddRuleModal
          key="categoryModal"
          title={i18n.t('spending_policies.wizard.steps.limits.add_category_modal.title')}
          description={i18n.t(
            'spending_policies.wizard.steps.limits.add_category_modal.description'
          )}
          multiple={false}
          handleSubmit={(categories: OptionItem[]) => {
            addCategories(categories)
            closeAddCategoryModal()
          }}
          handleClose={() => closeAddCategoryModal()}
          categories={unSelectedCategories}
        />
      )}
      {showAddSubcategoryModal && (
        <AddRuleModal
          key="categoryModal"
          title={i18n.t('spending_policies.wizard.steps.limits.add_subcategory_modal.title')}
          description={i18n.t(
            'spending_policies.wizard.steps.limits.add_subcategory_modal.description'
          )}
          multiple={false}
          handleSubmit={(subCategories: OptionItem[]) => {
            addSubCategories(subCategories)
            closeAddSubcategoryModal()
          }}
          handleClose={() => closeAddSubcategoryModal()}
          categories={subcategoriesNotSelected}
        />
      )}

      {!!ruleGroupToDelete && (
        <RemoveModal
          ruleGroup={ruleGroupToDelete}
          childRuleGroups={childRuleGroups.filter(
            (group) => group.parentRuleGroup?.id === ruleGroupToDelete.id
          )}
          companyId={spendingPolicy.companyId.toString()}
          closeModal={() => setRuleGroupToDelete(null)}
        />
      )}
    </Box>
  )
}

const createCategory = (
  category: OptionItem,
  spendingPolicy: EditSpendingPolicyDetailsFragment,
  currency: string,
  parentRuleGroup?: EditSpendingPolicyRuleGroupFragment
) => {
  return {
    companyId: spendingPolicy.companyId.toString(),
    spendingPolicyId: spendingPolicy.id.toString(),
    description: '',
    parentRuleGroup: parentRuleGroup,
    parentRuleGroupId: parentRuleGroup?.id.toString(),
    spendingPolicyRules: [
      {
        identifier: parentRuleGroup ? 'subcategory' : 'category',
        value: [category.value.toString()],
        formatedValue: category.label,
      },
      {
        identifier: 'amount',
        value: ['0'],
      },
      {
        identifier: 'currency',
        value: [currency],
      },
      {
        identifier: 'time_frame',
        value: [''],
      },
    ],
  }
}
